import { zodResolver } from '@hookform/resolvers/zod';
import {
  AlertTriangle,
  Eye,
  EyeOff,
  Landmark,
  Loader2,
  Lock,
  Mail,
  Moon,
  Receipt,
  ShieldCheck,
  Sun,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ApiError, login } from '../api/client';
import { isPreSessionState, type PreSessionState, type Stage } from '../api/schemas';
import { useFarbschema } from '../app/farbschema';
import { useFirmenname } from '../app/marke';
import { FameSignet } from '../ui/FameSignet';
import { Wortmarke } from '../ui/Wortmarke';
import { Button } from '../ui/elements';
import { Alert, AlertDescription, AlertTitle } from '../ui/shadcn/alert';
import { ANMELDE_INPUT_CLASS, AnmeldeFeld } from './felder';
import { PasswordChangeForm } from './PasswordChangeForm';
import { TotpEnrollForm } from './TotpEnrollForm';
import { TotpForm } from './TotpForm';

/**
 * Die Anmeldung — und mit ihr die Zustandskette aus `auth/pre-session.ts`.
 *
 * **Die Oberfläche entscheidet nichts.** Welcher Schritt als nächstes kommt,
 * sagt der Server: Jede Stufe antwortet entweder mit `authenticated` oder mit
 * einem Zustand. Die Kette hier nachzubauen — «nach dem Passwortwechsel kommt
 * die Einrichtung» — hiesse, dieselbe Regel ein zweites Mal zu schreiben, und
 * die zweite Fassung wäre irgendwann die falsche. Diese Komponente zeigt an,
 * was der Server als offen meldet, und sonst nichts.
 *
 * **Die Formulare prüfen die Eingabe, aber sie entscheiden nicht.** Die
 * Mindestlänge des Passworts steht auch im Dienst; die Prüfung hier erspart
 * eine Anfrage und ist kein Ersatz für sie. Die Schemas aus
 * `@fameacc/contracts` passen hier nicht: Dort stehen die Verträge der
 * Fachanfragen — Buchung und Storno —, keine Anmeldeformulare.
 *
 * ---------------------------------------------------------------------------
 * DIE BÜHNE — umgebaut nach `claude-design-concept/login_export/`
 * (Chip `W-loginredesign`)
 *
 * Bis hierher stand die Anmeldung als 448 px breite Karte in der Mitte einer
 * leeren Seite. Jetzt ist sie zweispaltig: links das Formular auf `--card`,
 * rechts eine Markenfläche auf `--primary`. Das Verhältnis (`1fr 1.08fr`), die
 * Feldhöhe (44 px), die Radien (10 px Feld und Knopf, 9 px Umschalter, 11 px
 * Meldung) und das Punktraster stehen in
 * `claude-design-concept/login_export/SPEC.md`.
 *
 * **Vier Dinge aus dem Konzept sind ABSICHTLICH nicht gebaut**, und keines
 * davon aus Zeitmangel — jedes würde etwas behaupten, das dieses System nicht
 * kann:
 *
 * 1. **«Angemeldet bleiben».** Die Sitzungslebensdauer ist in
 *    `apps/api/src/auth/session.ts` fest: 30 Minuten Ruhe (`IDLE_TTL_MS`),
 *    8 Stunden absolut (`ABSOLUTE_TTL_MS`). Es gibt keinen Endpunkt und kein
 *    Feld, mit dem der Browser daran etwas ändern könnte. Ein Häkchen, das
 *    nichts steuert, ist eine Lüge mit Fokusring.
 * 2. **Der Versuchszähler («Noch 3 Versuche bis zur Sperrung»).** Die Sperre
 *    gibt es serverseitig — fünf Fehlversuche, fünfzehn Minuten
 *    (`auth.service.ts`, `MAX_FAILED_ATTEMPTS`/`LOCK_MINUTES`) —, aber die
 *    Antwort trägt den Stand des Zählers NICHT: Sie unterscheidet nur
 *    `AUTH_FAILED` von `ACCOUNT_LOCKED`. Ihn im Browser mitzuzählen ergäbe
 *    eine Zahl, die nach einem Neuladen falsch ist, und verriete nebenbei
 *    einem Angreifer, wie weit er ist.
 * 3. **«Mit Firmen-SSO anmelden».** Es gibt keinen Identitätsanbieter. Das
 *    Konzept verlangt das selbst so — «kein toter Button im Produktivsystem».
 * 4. **«Vergessen?» und die drei Fusszeilenverweise** (Datenschutz, Impressum,
 *    Status). Zu keinem davon führt eine Adresse in `app/router.ts`; sie
 *    landeten sämtlich im Journal. Aus demselben Grund fehlt der `mailto:` an
 *    den IT-Support: Diese Adresse steht an keiner Stelle des Repositories,
 *    sie wäre erfunden.
 *
 * **Die Fehlermeldung bleibt unspezifisch, und das kommt nicht von hier.**
 * `AUTH_FAILED` heisst im Dienst «E-Mail-Adresse oder Passwort ist falsch.»
 * (`apps/api/src/common/error-codes.ts`) — unbekannte Adresse, falsches
 * Passwort und deaktiviertes Konto führen dort zur selben Antwort. Die
 * Oberfläche zeigt an, was kommt, und formuliert nichts nach.
 *
 * **`?next=` ist nicht gebaut, weil es hier nichts zu bauen gibt.** Diese
 * Komponente ersetzt in `app/App.tsx` die ganze Anwendung, ohne die Adresszeile
 * anzufassen; wer `/belege/<id>` aufruft und dabei angemeldet werden muss,
 * steht nach dem letzten Schritt wieder auf `/belege/<id>` — `useNavigation()`
 * liest dieselbe, nie veränderte Adresse. Ein Parameter dafür wäre ein zweiter
 * Weg zum selben Ziel.
 */

/** Das Anmeldeformular. Die Prüfung, ob es die Adresse gibt, macht der Server. */
const credentialsSchema = z.object({
  email: z.string().min(1, 'Bitte die E-Mail-Adresse eingeben.'),
  password: z.string().min(1, 'Bitte das Passwort eingeben.'),
});

/** Die Eingaben der ersten Stufe. */
type Credentials = z.infer<typeof credentialsSchema>;

/** Der Schritt, der gerade offen ist. */
type Step =
  { readonly kind: 'credentials' } | { readonly kind: PreSessionState; readonly password: string };

export function LoginPage(props: {
  readonly onSignedIn: () => void;
  /**
   * Eine Meldung, die von AUSSEN kommt — der Satz aus `onReauthRequired`, wenn
   * das Passwort auf einem zweiten Gerät gewechselt wurde.
   *
   * **Sie steht bewusst hier drin und nicht darüber.** Vor dem Umbau setzte
   * `App.tsx` seinen eigenen Meldungskasten ÜBER die Anmeldemaske; auf einer
   * Seite, die jetzt den ganzen Bildschirm einnimmt, schöbe er die Maske nach
   * unten aus dem Bild. Ein eigener, lokaler Fehlschlag verdrängt ihn: Wer
   * gerade eine falsche Eingabe gemacht hat, will nicht lesen, warum er vorhin
   * abgemeldet wurde.
   */
  readonly hinweis?: string;
}): ReactNode {
  const [step, setStep] = useState<Step>({ kind: 'credentials' });
  const [failure, setFailure] = useState<ApiError | undefined>(undefined);
  const farbschema = useFarbschema();
  const dunkel = farbschema.schema === 'dunkel';
  // Seit `PF-lizenzname` (`E-1731`) zuerst der Kundenname der hinterlegten
  // Lizenz, sonst der Name der geltenden Ausstellerfassung. `null` ohne beide
  // (Einrichtung offen, keine Lizenz), solange die Antwort unterwegs ist oder
  // wenn sie fehlschlug: dann zeigt die Seite nur den Produktnamen
  // (`PF-marke`, `E-1657`). Bis zu 200 Zeichen — jede der drei Stellen kürzt
  // mit Auslassung und trägt den vollen Namen im `title`.
  const firmenname = useFirmenname();

  /**
   * Wertet die Antwort einer Stufe aus.
   *
   * Ein Zustand, den diese Fassung der Oberfläche nicht kennt, führt zurück
   * auf die Anmeldemaske und nicht auf einen leeren Bildschirm: Der Server ist
   * dann neuer als das Frontend, und der Anwender soll das merken.
   */
  const advance = (stage: Stage, password: string): void => {
    if (stage.status === 'authenticated') {
      props.onSignedIn();
      return;
    }
    if (isPreSessionState(stage.state)) {
      setFailure(undefined);
      setStep({ kind: stage.state, password });
      return;
    }
    setStep({ kind: 'credentials' });
    setFailure(
      new ApiError(
        200,
        'UNKNOWN_STATE',
        'Die Anmeldung verlangt einen Schritt, den diese Oberfläche nicht kennt.',
        undefined,
      ),
    );
  };

  const onFailure = (error: unknown): void => {
    setFailure(toApiError(error));
  };

  return (
    <main className="grid min-h-screen lg:grid-cols-[1fr_1.08fr]">
      {/* Die Formularspalte. Kopfzeile oben, Formular mittig, Fusszeile unten. */}
      <div className="flex flex-col bg-card px-5 py-7 sm:px-7 sm:py-10 lg:px-14 lg:py-11">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <FameSignet className="size-[34px] shrink-0" />
            <div className="flex flex-col leading-[1.15]">
              <Wortmarke className="text-foreground" />
            </div>
          </div>
          {/*
            Der Farbschema-Umschalter steht auch hier und nicht erst hinter der
            Anmeldung: Wer im Dunkeln arbeitet, bekäme sonst ausgerechnet den
            einen Bildschirm dieser Anwendung mit der grössten hellen Fläche
            ungefragt hell. `title` trägt den zugänglichen Namen, weil der Knopf
            nur ein Sinnbild zeigt — dasselbe Muster wie «Neu» in
            `app/Kopfzeile.tsx`.
          */}
          <Button
            variant="secondary"
            size="header"
            className="w-[34px] rounded-[9px] px-0"
            title={dunkel ? 'Helle Darstellung' : 'Dunkle Darstellung'}
            onClick={farbschema.umschalten}
          >
            {dunkel ? (
              <Sun aria-hidden="true" className="size-[18px]" />
            ) : (
              <Moon aria-hidden="true" className="size-[18px]" />
            )}
          </Button>
        </div>

        {/*
          **Der Block steht senkrecht mittig zwischen Kopf- und Fusszeile**
          (`SPEC.md`, «Layout»). Das verlangt eine wachsende Mitte —
          `flex-1` mit `items-center` — und nicht `my-12` plus `mt-auto` am
          Fuss: Damit sässe das Formular unmittelbar unter der Kopfzeile,
          und der Fuss allein am unteren Rand. Im Browser nachgesehen.
        */}
        <div className="flex flex-1 items-center justify-center py-12">
          <div className="w-full max-w-[400px]">
            <h1 className="mb-2 text-[27px] font-semibold tracking-[-0.028em] text-foreground">
              {TITEL[step.kind]}
            </h1>
            <p
              className="mb-[30px] line-clamp-3 break-words text-pretty text-sm text-muted-foreground"
              title={step.kind === 'credentials' && firmenname !== null ? firmenname : undefined}
            >
              {unterzeile(step.kind, firmenname)}
            </p>

            {failure === undefined ? (
              props.hinweis === undefined ? null : (
                <Meldung titel="Die Anmeldung ist erneut nötig" text={props.hinweis} />
              )
            ) : (
              <Meldung
                titel="Anmeldung fehlgeschlagen"
                text={failure.message}
                {...(failure.correlationId === undefined
                  ? {}
                  : { correlationId: failure.correlationId })}
              />
            )}

            {step.kind === 'credentials' ? (
              <CredentialsForm onStage={advance} onFailure={onFailure} />
            ) : null}

            {step.kind === 'awaiting_password_change' ? (
              <PasswordChangeForm
                buehne="anmeldung"
                currentPassword={step.password}
                onStage={(stage, password) => {
                  advance(stage, password);
                }}
                onFailure={onFailure}
              />
            ) : null}

            {step.kind === 'awaiting_totp_enroll' ? (
              <TotpEnrollForm
                onStage={(stage) => {
                  advance(stage, step.password);
                }}
                onFailure={onFailure}
              />
            ) : null}

            {step.kind === 'awaiting_totp' ? (
              <TotpForm
                onStage={(stage) => {
                  advance(stage, step.password);
                }}
                onFailure={onFailure}
              />
            ) : null}

            {step.kind === 'credentials' ? (
              <p className="mt-[26px] text-pretty text-[12.5px] text-text3">
                Kein Zugang? Die Freigabe erfolgt über die Geschäftsführung.
              </p>
            ) : null}
          </div>
        </div>

        {/*
          **Der Satz im Fuss ist eine Tatsache und keine Beruhigung.**
          `auth.service.ts` schreibt zu JEDER Anmeldung — geglückt wie
          gescheitert — eine Zeile ins `audit_log`, und `contextOf()`
          (`capability.guard.ts`) legt die IP-Adresse dazu. Ein User-Agent wird
          NICHT erfasst; deshalb steht hier nicht «Gerät wird registriert», wie
          das Konzept vorschlägt, sondern das, was wirklich gespeichert wird.
          Wer den Satz ändert, ändert eine Aussage mit Datenschutzbezug.
        */}
        <div className="flex flex-wrap items-center justify-between gap-4 text-xs text-text3">
          <span className="min-w-0 max-w-full truncate" title={firmenname ?? undefined}>
            {firmenname}
          </span>
          <span className="flex items-center gap-1.5">
            <ShieldCheck aria-hidden="true" className="size-[13px]" />
            Jede Anmeldung wird mit IP-Adresse protokolliert
          </span>
        </div>
      </div>

      <Markenspalte firmenname={firmenname} />
    </main>
  );
}

/**
 * Die Überschrift, je Schritt.
 *
 * **Sie ist neu; die Unterzeile darunter ist es nicht.** Bis zum Umbau stand
 * über allen vier Schritten dieselbe Zeile «FameAccounting», und der Schritt
 * stand allein in der Unterzeile. Die Überschrift sagt jetzt den Schritt, die
 * Unterzeile die Anweisung.
 *
 * **`credentials` heisst «Anmelden», obwohl darunter ein Knopf mit demselben
 * Wort steht.** Das ist geprüft und kein Problem: `e2e/harness.ts` und
 * `e2e/first-login.spec.ts` greifen über `getByRole('button', …)`, und eine
 * Überschrift hat diese Rolle nicht. Für den zweiten Faktor steht trotzdem
 * «Zweiter Faktor» und nicht «Bestätigen» — es beschreibt den Schritt und
 * nicht die Taste.
 */
const TITEL: Readonly<Record<Step['kind'], string>> = {
  credentials: 'Anmelden',
  awaiting_password_change: 'Passwort wechseln',
  awaiting_totp_enroll: 'Zweiter Faktor',
  awaiting_totp: 'Zweiter Faktor',
};

/**
 * Die Zeile unter der Überschrift, je Schritt.
 *
 * **Drei dieser vier Sätze sind WÖRTLICH unverändert**, und das ist keine
 * Bequemlichkeit: `e2e/harness.ts` wartet auf «Einmalkennwort eingeben.» und
 * `e2e/first-login.spec.ts` auf «Einmalkennwort einrichten.», beide über
 * `getByText`. Wer hier umformuliert, legt die halbe Suite still — jede Spec
 * dieses Verzeichnisses läuft durch `signIn`.
 */
const UNTERZEILE: Readonly<Record<Step['kind'], string>> = {
  credentials: 'Zugang nur für berechtigte Mitarbeitende.',
  awaiting_password_change: 'Das Erstpasswort muss gewechselt werden.',
  awaiting_totp_enroll: 'Einmalkennwort einrichten.',
  awaiting_totp: 'Einmalkennwort eingeben.',
};

/**
 * Die Zeile unter der Überschrift, mit dem Firmennamen, wo es einen gibt.
 *
 * Im ersten Schritt steht mit Namen «Buchhaltung der <Firma>. Zugang nur für
 * berechtigte Mitarbeitende.», ohne ihn nur der zweite Satz — vor dem Abschluss
 * der Einrichtung kennt die Seite nur den Produktnamen (`PF-marke`, `E-1657`).
 * Die anderen drei Schritte tragen keinen Namen und bleiben wörtlich (siehe
 * oben, die Suite wartet auf sie).
 */
function unterzeile(schritt: Step['kind'], firmenname: string | null): string {
  if (schritt === 'credentials' && firmenname !== null) {
    return `Buchhaltung der ${firmenname}. ${UNTERZEILE.credentials}`;
  }
  return UNTERZEILE[schritt];
}

/**
 * Der Meldungskasten der Anmeldestrecke.
 *
 * **Warum nicht `ErrorBox` aus `ui/elements.tsx`.** Der Kasten dort setzt den
 * Meldungstext als TITEL — auf einer Seite mit acht Kästen ist das richtig,
 * dort ist die Meldung die ganze Nachricht. Hier steht sie allein auf einer
 * leeren Bühne, und das Konzept verlangt zwei Ebenen: eine Zeile, die sagt,
 * was passiert ist («Anmeldung fehlgeschlagen»), und darunter, was der Server
 * dazu sagt (`claude-design-concept/login_export/SPEC.md`, Zustand «Fehler»).
 *
 * **`role="alert"` kommt aus dem Baustein** und macht den Kasten zur
 * Live-Region — ein Vorleseprogramm sagt die Meldung an, ohne dass jemand
 * dorthin navigiert.
 *
 * **Die Fläche ist `--fa-danger-soft` und nicht `--destructive/10`.** Das ist
 * der Wert aus dem Übergabepaket, und er ist gemessen: `--destructive`
 * (= `--fa-danger`) darauf hält hell **5,72:1** und dunkel **6,10:1**
 * (`src/styles.css`, Tabelle «Nachgerechnete Paare»). Die Kante bleibt
 * `--destructive` mit halber Deckung.
 */
function Meldung(props: {
  readonly titel: string;
  readonly text: string;
  readonly correlationId?: string;
}): ReactNode {
  return (
    <Alert
      variant="destructive"
      className="mb-5 rounded-[11px] border-destructive/50 bg-danger-soft has-[>svg]:grid-cols-[18px_1fr] [&>svg]:size-[18px]"
    >
      <AlertTriangle aria-hidden="true" />
      <AlertTitle>{props.titel}</AlertTitle>
      <AlertDescription>
        <span>{props.text}</span>
        {props.correlationId === undefined ? null : (
          <span className="text-xs">
            Vorgangskennung: <span className="zahl">{props.correlationId}</span>
          </span>
        )}
      </AlertDescription>
    </Alert>
  );
}

/** Erste Stufe: E-Mail und Passwort. */
function CredentialsForm(props: {
  readonly onStage: (stage: Stage, password: string) => void;
  readonly onFailure: (error: unknown) => void;
}): ReactNode {
  const form = useForm<Credentials>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { email: '', password: '' },
  });
  const [sichtbar, setSichtbar] = useState(false);

  const submit = async (values: Credentials): Promise<void> => {
    try {
      props.onStage(await login(values.email, values.password), values.password);
    } catch (error) {
      props.onFailure(error);
    }
  };

  return (
    <form
      className="flex flex-col gap-4"
      noValidate
      onSubmit={(event) => {
        void form.handleSubmit(submit)(event);
      }}
    >
      {/*
        **Die Beschriftungen heissen weiter «E-Mail-Adresse» und «Passwort».**
        Das Konzept schreibt «Geschäftliche E-Mail»; der zugängliche Name ist
        aber der Griff, mit dem `e2e/harness.ts` und `e2e/first-login.spec.ts`
        das Feld finden — und `harness.signIn` benutzt JEDE Spec dieses
        Verzeichnisses. Ein anderes Wort für dieselbe Sache ist kein Gewinn,
        der dreissig Prüfläufe wert wäre.
      */}
      <div>
        <AnmeldeFeld label="E-Mail-Adresse" sinnbild={<Mail />}>
          <input
            type="email"
            autoComplete="username"
            autoFocus
            placeholder="name@beispiel.de"
            className={ANMELDE_INPUT_CLASS}
            {...form.register('email')}
          />
        </AnmeldeFeld>
        <FieldError message={form.formState.errors.email?.message} />
      </div>

      <div>
        <AnmeldeFeld
          label="Passwort"
          sinnbild={<Lock />}
          hinten={
            <button
              type="button"
              // Kein `tabIndex={-1}`: Der Umschalter ist mit der Tastatur
              // erreichbar, weil er sonst für jemanden ohne Maus nicht
              // existierte. Er steht NACH dem Feld im Baum und liegt damit auch
              // in der Tabulatorfolge dahinter.
              aria-label={sichtbar ? 'Passwort verbergen' : 'Passwort anzeigen'}
              aria-pressed={sichtbar}
              onClick={() => {
                setSichtbar((vorher) => !vorher);
              }}
              className="-mr-1 rounded-sm p-1 text-text3 outline-none hover:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
            >
              {sichtbar ? (
                <EyeOff aria-hidden="true" className="size-[18px]" />
              ) : (
                <Eye aria-hidden="true" className="size-[18px]" />
              )}
            </button>
          }
        >
          {/*
            `type` wechselt, `autoComplete` nicht. Ein Passwortverwalter
            erkennt das Feld an `current-password` und nicht am Typ; wer ihm
            beim Umschalten den Namen wegnimmt, verliert das Ausfüllen genau
            dann, wenn jemand nachsehen wollte, was er getippt hat.
          */}
          <input
            type={sichtbar ? 'text' : 'password'}
            autoComplete="current-password"
            className={ANMELDE_INPUT_CLASS}
            {...form.register('password')}
          />
        </AnmeldeFeld>
        <FieldError message={form.formState.errors.password?.message} />
      </div>

      {/*
        **Der Knopf ist 44 px hoch und füllt die Breite** — die Vorgabe des
        Bausteins ist 32 px, und die gilt für Knöpfe INNERHALB einer Tafel
        (Begründung in `ui/elements.tsx`). Dies ist die eine Handlung eines
        Bildschirms, der sonst nichts anbietet; `SPEC.md` nennt 44 px und
        Radius 10 px.
      */}
      <Button
        type="submit"
        disabled={form.formState.isSubmitting}
        className="mt-1.5 h-11 w-full rounded-[10px] text-sm font-semibold"
      >
        {form.formState.isSubmitting ? (
          <>
            <Loader2 aria-hidden="true" className="size-[15px] animate-spin" />
            Anmeldung läuft …
          </>
        ) : (
          'Anmelden'
        )}
      </Button>
    </form>
  );
}

/** Die Meldung eines einzelnen Feldes. Steht unter dem Feld, nicht daneben. */
export function FieldError(props: { readonly message: string | undefined }): ReactNode {
  if (props.message === undefined) return null;
  return <p className="mt-[7px] text-sm text-destructive">{props.message}</p>;
}

/**
 * Macht aus einem geworfenen Wert einen `ApiError`.
 *
 * Ein abgebrochener Netzzugriff wirft einen `TypeError` und keinen `ApiError`;
 * ohne diese Umwandlung stünde auf dem Bildschirm «failed to fetch» statt
 * eines Satzes, mit dem jemand etwas anfangen kann.
 */
export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  return new ApiError(
    0,
    'NETWORK_ERROR',
    'Der Server ist nicht erreichbar. Bitte später erneut versuchen.',
    undefined,
  );
}

/* ===========================================================================
 * DIE MARKENSPALTE
 *
 * Sie ist Fläche und trägt nichts, was jemand bedienen könnte — unter 1024 px
 * verschwindet sie ersatzlos (`hidden lg:flex`), und dabei geht keine Funktion
 * verloren.
 *
 * **Was hier steht, ist wahr, und das ist gegen die Vorlage entschieden.** Das
 * Übergabepaket füllt den Fuss mit drei Kennzahlen — «2.418 Belege 2026»,
 * «99,4 % Automatisch zugeordnet», «< 2 s Ø Erkennungszeit» — und die
 * Merkmalsliste mit «Belegerkennung mit Kontovorschlag» und «DATEV-Export auf
 * Knopfdruck». Keine dieser fünf Aussagen trifft auf dieses System zu: Es gibt
 * keine Belegerkennung (der Nordstern verbietet sie ausdrücklich, solange
 * nicht gemessen ist), keinen DATEV-Export (letzte Ära) und keine Zahlen, die
 * jemand nachschlagen könnte. Erfundene Kennzahlen auf dem Anmeldebildschirm
 * eines Buchhaltungssystems sind kein Schmuck, sondern eine Behauptung — und
 * ausgerechnet dieses System misst sich daran, dass jede seiner Aussagen
 * belegbar ist.
 *
 * An ihrer Stelle stehen drei Fähigkeiten, die es heute gibt, und drei
 * Angaben, die konstant und nachprüfbar sind: der Kontenrahmen (SKR03-Seed,
 * `packages/schema`), die GoBD-Verfahrensdokumentation
 * (`docs/verfahrensdokumentation/`, zehn Kapitel) und der zweite Faktor
 * (`apps/api/src/auth/totp.ts`, in der Kette nicht überspringbar).
 *
 * ---------------------------------------------------------------------------
 * DIE DECKUNGSWERTE SIND NACHGERECHNET UND IM DUNKELN KORRIGIERT
 *
 * Das Konzept setzt den Text dieser Spalte gedämpft: 0,6 für die Kopfzeile,
 * 0,8 für den Absatz, 0,66 für die kleinen Zeilen im Fuss. Gemessen
 * (`--primary-foreground` mit dieser Deckung über `--primary`, WCAG 2.1,
 * Grenze 4,5:1 für Text) hält das im Hellen und im Dunkeln NICHT:
 *
 *   Deckung   0,60   0,66   0,70   0,80   0,85   0,90   1,00
 *   hell      4,05   4,64   5,05   6,24   6,89   7,58   9,09
 *   dunkel    2,93   3,27   3,51   4,18   4,52   4,85   5,45
 *
 * Im Dunkeln ist `--primary` die HELLE Fläche und `--primary-foreground` der
 * dunkle Text — jede Dämpfung zieht ihn zur Fläche hin, und unter 0,85 fällt
 * er durch. Korrigiert ist deshalb nur die dunkle Fassung, damit das helle
 * Bild dem Konzept entspricht: 0,60 → `opacity-70 dark:opacity-[0.85]`,
 * 0,80 → `opacity-80 dark:opacity-90`, 0,66 →
 * `opacity-[0.66] dark:opacity-[0.85]`. Der einzige hell angehobene Wert ist
 * die Kopfzeile (0,60 → 0,70), weil 4,05:1 auch dort durchfällt.
 *
 * Was KEINE Textprüfung braucht: Punktraster, Balkenreihe und die 15 % weisse
 * Kachel hinter den drei Sinnbildern — die Kachel misst gegen die Fläche nur
 * 1,35:1 (hell) beziehungsweise 1,22:1 (dunkel), trägt aber keine Grenze: Das
 * Sinnbild DARAUF misst 6,73:1 und 6,63:1, und die Kachel selbst ist Zier.
 * =========================================================================== */

/** Die drei Merkmale. Jedes hat heute eine Seite in dieser Anwendung. */
const MERKMALE: readonly { readonly Sinnbild: typeof Landmark; readonly text: string }[] = [
  { Sinnbild: Landmark, text: 'Bankumsätze einlesen und abgleichen' },
  { Sinnbild: Receipt, text: 'Eingangsrechnungen erfassen und archivieren' },
  { Sinnbild: ShieldCheck, text: 'Journal nach GoBD, unveränderlich' },
];

/** Die drei Angaben im Fuss. Konstant, nachprüfbar, ohne Datenbestand. */
const ANGABEN: readonly { readonly wert: string; readonly text: string }[] = [
  { wert: 'SKR 03', text: 'Kontenrahmen' },
  { wert: 'GoBD', text: 'Verfahrensdokumentiert' },
  { wert: '2FA', text: 'Bei jeder Anmeldung' },
];

/** Die Höhen der Balkenreihe, in Pixel. Reine Zier, siehe `Balken`. */
const BALKEN = [34, 52, 41, 68, 58, 82, 74, 96, 88];

/**
 * Das Punktraster oben rechts.
 *
 * Die Punkte wachsen zur oberen rechten Ecke hin: `t = 1 − hypot(dx, dy)/1,15`
 * über die normierte Entfernung, unter 0,05 entfällt der Punkt (`SPEC.md`,
 * «Brand-Spalte»). Gerechnet statt gezeichnet, weil 96 von Hand gesetzte
 * Positionen eine Datei wären, die niemand mehr ändert.
 *
 * **`bg-white` und kein Token, absichtlich.** Es liegt IMMER auf `--primary`
 * und nie auf einer anderen Fläche; ein Token dafür hiesse, eine Rolle zu
 * erfinden, die genau einen Verwendungsort hat. Dieselbe Begründung wie beim
 * Schleier des Dialogs (`ui/elements.tsx`).
 */
function Punktraster(): ReactNode {
  const spalten = 12;
  const zeilen = 8;
  const punkte: {
    readonly top: number;
    readonly left: number;
    readonly groesse: number;
    readonly deckung: number;
  }[] = [];

  for (let zeile = 0; zeile < zeilen; zeile++) {
    for (let spalte = 0; spalte < spalten; spalte++) {
      const entfernung = Math.hypot((spalten - 1 - spalte) / (spalten - 1), zeile / (zeilen - 1));
      const t = Math.max(0, 1 - entfernung / 1.15);
      if (t < 0.05) continue;
      const groesse = 3 + t * 12;
      punkte.push({
        top: zeile * 26 + (18 - groesse) / 2,
        left: spalte * 28 + (18 - groesse) / 2,
        groesse,
        deckung: 0.08 + t * 0.34,
      });
    }
  }

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute right-0 top-0 h-[210px] w-[340px]"
    >
      {punkte.map((punkt) => (
        <span
          key={`${String(punkt.top)}-${String(punkt.left)}`}
          className="absolute rounded-pille bg-white"
          style={{
            top: punkt.top,
            left: punkt.left,
            width: punkt.groesse,
            height: punkt.groesse,
            opacity: punkt.deckung,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Die Balkenreihe im Fuss.
 *
 * **Sie stellt nichts dar und sagt das auch: `aria-hidden`.** Ein Diagramm
 * ohne Daten wäre eine Aussage über Zahlen, die es nicht gibt — als Zier ist
 * es eine Form. Genau deshalb steht daneben keine Achse und keine Einheit.
 */
function Balken(): ReactNode {
  return (
    <div aria-hidden="true" className="flex items-end gap-1">
      {BALKEN.map((hoehe, index) => (
        <span
          key={hoehe}
          className="w-1.5 rounded-t-[3px] bg-white"
          style={{ height: hoehe, opacity: 0.2 + index * 0.055 }}
        />
      ))}
    </div>
  );
}

/** Die rechte Spalte. Ab 1024 px sichtbar, darunter gar nicht vorhanden. */
function Markenspalte(props: { readonly firmenname: string | null }): ReactNode {
  return (
    <div className="relative hidden flex-col justify-between overflow-hidden bg-primary p-11 text-primary-foreground lg:flex xl:p-14">
      <Punktraster />

      <div
        className="relative truncate font-zahl text-[10.5px] uppercase tracking-[0.2em] opacity-70 dark:opacity-[0.85]"
        title={props.firmenname ?? undefined}
      >
        {props.firmenname === null ? 'Buchhaltung' : `Buchhaltung · ${props.firmenname}`}
      </div>

      <div className="relative max-w-[460px]">
        <h2 className="mb-4 text-pretty text-[34px] font-semibold leading-[1.16] tracking-[-0.032em]">
          Belege rein, Buchung fertig.
        </h2>
        <p className="mb-[34px] text-pretty text-[15px] leading-relaxed opacity-80 dark:opacity-90">
          Bankumsätze, Eingangsrechnungen und Belegwesen in einem System. Jede Buchung entsteht
          einmal, an der Stelle, an der der Beleg ankommt — und bleibt danach, wie sie ist.
        </p>
        <ul className="flex flex-col gap-3">
          {MERKMALE.map(({ Sinnbild, text }) => (
            <li key={text} className="flex items-center gap-3">
              <span className="grid size-[22px] shrink-0 place-items-center rounded-[7px] bg-white/15">
                <Sinnbild aria-hidden="true" className="size-[13px]" strokeWidth={2} />
              </span>
              <span className="text-sm opacity-90">{text}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="relative flex items-end justify-between gap-6">
        <div className="flex gap-[30px]">
          {ANGABEN.map((angabe) => (
            <div key={angabe.text}>
              <div className="font-zahl text-[22px] font-medium tracking-[-0.03em]">
                {angabe.wert}
              </div>
              <div className="mt-[3px] text-[11.5px] opacity-[0.66] dark:opacity-[0.85]">
                {angabe.text}
              </div>
            </div>
          ))}
        </div>
        <Balken />
      </div>
    </div>
  );
}
